//----------------------------------------------------------------------------------------------------------------------
// GameTimer - Pause-aware timer utilities
//----------------------------------------------------------------------------------------------------------------------

/**
 * A pollable cooldown handle.
 */
export interface CooldownHandle
{
    readonly ready : boolean;
    reset() : void;
}

//----------------------------------------------------------------------------------------------------------------------

interface DelayEntry
{
    id : number;
    remaining : number;
    callback : () => void;
}

interface IntervalEntry
{
    id : number;
    periodMs : number;
    elapsed : number;
    callback : () => void;
}

interface CooldownEntry
{
    id : number;
    periodMs : number;
    remaining : number;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Pause-aware game timer providing delay, interval, and cooldown primitives.
 *
 * All times are in game-time milliseconds. When the game is paused, `tick()` is simply not called, so all timers
 * freeze automatically.
 */
export class GameTimer
{
    private _nextId = 0;
    private _delays : DelayEntry[] = [];
    private _intervals : IntervalEntry[] = [];
    private _cooldowns : CooldownEntry[] = [];

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * One-shot timer. Fires the callback after `ms` milliseconds of game time.
     *
     * @returns A cancel function that prevents the callback from firing.
     */
    delay(ms : number, callback : () => void) : () => void
    {
        if(ms < 0)
        {
            throw new RangeError(`delay() requires ms >= 0, got ${ ms }`);
        }

        const id = this._nextId++;
        this._delays.push({ id, remaining: ms, callback });

        return () =>
        {
            this._delays = this._delays.filter((entry) => entry.id !== id);
        };
    }

    /**
     * Repeating timer. Fires the callback every `ms` milliseconds of game time.
     *
     * @returns A cancel function that stops the interval.
     */
    interval(ms : number, callback : () => void) : () => void
    {
        if(ms <= 0)
        {
            throw new RangeError(`interval() requires ms > 0, got ${ ms }`);
        }

        const id = this._nextId++;
        this._intervals.push({ id, periodMs: ms, elapsed: 0, callback });

        return () =>
        {
            this._intervals = this._intervals.filter((entry) => entry.id !== id);
        };
    }

    /**
     * Pollable cooldown. Starts ready; after calling `reset()`, becomes not-ready until `ms` elapses.
     */
    cooldown(ms : number) : CooldownHandle
    {
        const id = this._nextId++;
        const entry : CooldownEntry = { id, periodMs: ms, remaining: 0 };
        this._cooldowns.push(entry);

        return {
            get ready() : boolean
            {
                return entry.remaining <= 0;
            },
            reset() : void
            {
                entry.remaining = entry.periodMs;
            },
        };
    }

    /**
     * Cancel all active delays, intervals, and cooldowns.
     */
    cancelAll() : void
    {
        this._delays = [];
        this._intervals = [];

        // Set all cooldowns to ready before clearing, so existing CooldownHandle references aren't stuck
        for(const entry of this._cooldowns)
        {
            entry.remaining = 0;
        }

        this._cooldowns = [];
    }

    /**
     * Advance all timers by `dtMs` milliseconds. Called once per frame by the game loop.
     */
    tick(dtMs : number) : void
    {
        // Process delays
        const firedDelays : number[] = [];
        for(const entry of this._delays)
        {
            entry.remaining -= dtMs;
            if(entry.remaining <= 0)
            {
                entry.callback();
                firedDelays.push(entry.id);
            }
        }

        if(firedDelays.length > 0)
        {
            this._delays = this._delays.filter((entry) => !firedDelays.includes(entry.id));
        }

        // Process intervals
        for(const entry of this._intervals)
        {
            entry.elapsed += dtMs;
            while(entry.elapsed >= entry.periodMs)
            {
                entry.callback();
                entry.elapsed -= entry.periodMs;
            }
        }

        // Process cooldowns
        for(const entry of this._cooldowns)
        {
            if(entry.remaining > 0)
            {
                entry.remaining = Math.max(0, entry.remaining - dtMs);
            }
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
